<template>
    <div class="row">
        <div class="col-8">
            <div class="card card-gg">
                <div class="card-header">
                    Pay
                </div>
                <div class="tab-content">
                    <div class="tab-pane bg-white card-body active">
                        <div class="form-group">
                            <div id="payment-element"></div>
                            <div
                                v-if="errorMessage"
                                role="alert"
                                class="invalid-feedback d-block"
                                v-html="errorMessage"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-4">
            <div class="card card-gg card-summary">
                <div class="card-body">
                    <h4>Order Summary</h4>
                    <table class="table" v-if="cart">
                        <thead>
                        <tr>
                            <th colspan="2">Team / Venue (A-Z)</th>
                            <th>$ Amount</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                            v-for="team in cart.teams"
                            :key="team.team.id"
                            v-if="team.total > 0"
                        >
                            <td>
                                <img
                                    :alt="team.team.name"
                                    :src="team.team.photo_url"
                                    height="24"
                                    width="24"
                                    class="mr-1"
                                />
                            </td>
                            <td>{{ team.team.name }}</td>
                            <td class="team-price">{{ formatMoney(team.total) }}</td>
                        </tr>
                        </tbody>
                    </table>
                    <div v-if="cart" class="team-count">
                        {{ cart.team_count }} Teams | {{ cart.ready_rows_count }}
                        {{ cart.ready_rows_count === 1 ? 'Asset' : 'Assets' }}
                    </div>
                    <div v-if="cart" class="total-cost">
                        Total Cost: <span class="money">{{ formatMoney(cart.total) }}</span>
                    </div>
                    <button
                        id="card-button"
                        class="btn btn-secondary w-100"
                        @click="submitOrder"
                        :disabled="disabled || isProcessing"
                        type="submit"
                    >
                        {{ isProcessing ? 'PROCESSING...' : 'SUBMIT ORDER' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            disabled: true,
            paymentElement: null,
            isProcessing: false,
            errorMessage: '',
            stripe: null,
            elements: null,
            clientSecret: '',
            paymentIntentId: '',
            cart: null,
            isLoadingCart: true,
        };
    },

    props: {
        stripeKey: String,
    },

    async mounted() {
        await this.loadCart();
        await this.initializeStripe();
        this.setupEventListeners();
    },

    beforeDestroy() {
        this.cleanupEventListeners();
    },

    methods: {
        async loadCart() {
            try {
                const response = await axios.get('/api/checkout/cart');
                this.cart = response.data.data;
                this.isLoadingCart = false;
            } catch (error) {
                console.error('Failed to load cart:', error);
                this.errorMessage = 'Failed to load cart data.';
                this.isLoadingCart = false;
            }
        },

        async initializeStripe() {
            try {
                // Create payment intent
                const response = await axios.post('/api/checkout/payment-intent');
                this.clientSecret = response.data.data.client_secret;
                this.paymentIntentId = response.data.data.payment_intent_id;

                // Initialize Stripe with appearance customization
                this.stripe = Stripe(this.stripeKey);
                this.elements = this.stripe.elements({
                    clientSecret: this.clientSecret,
                    appearance: {
                        theme: 'stripe',
                        variables: {
                            colorPrimary: '#28a745',
                            colorBackground: '#ffffff',
                            colorText: '#30313d',
                            colorDanger: '#df1b41',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                            fontSizeBase: '16px',
                            spacingUnit: '4px',
                            borderRadius: '4px',
                            colorTextSecondary: '#6c757d',
                            colorTextPlaceholder: '#adb5bd',
                        },
                        rules: {
                            '.Input': {
                                border: '1px solid #ced4da',
                                boxShadow: 'none',
                                padding: '10px 12px',
                            },
                            '.Input:focus': {
                                border: '1px solid #28a745',
                                boxShadow: '0 0 0 0.2rem rgba(40, 167, 69, 0.25)',
                            },
                            '.Input--invalid': {
                                border: '1px solid #df1b41',
                            },
                            '.Label': {
                                fontWeight: '500',
                                marginBottom: '8px',
                            },
                            '.Tab': {
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                            },
                            '.Tab:hover': {
                                borderColor: '#28a745',
                            },
                            '.Tab--selected': {
                                borderColor: '#28a745',
                                backgroundColor: '#f8fff9',
                            },
                        },
                    },
                });

                // Create and mount payment element
                this.paymentElement = this.elements.create('payment');
                this.paymentElement.mount('#payment-element');

                // Listen for completion
                this.paymentElement.on('change', (event) => {
                    this.disabled = !event.complete;
                });

            } catch (error) {
                console.error('Failed to initialize Stripe:', error);
                this.errorMessage = 'Failed to initialize payment system.';
            }
        },

        async submitOrder() {
            if (this.disabled || this.isProcessing) {
                return;
            }

            this.isProcessing = true;
            this.errorMessage = '';

            try {
                // Create the order with payment intent
                const orderResponse = await axios.post('/api/checkout/orders', {
                    payment_intent_id: this.paymentIntentId,
                });

                const order = orderResponse.data.data;
                const confirmationUrl = `/advertiser/checkout/${order.id}`;

                // Confirm payment with Stripe
                const { error } = await this.stripe.confirmPayment({
                    elements: this.elements,
                    confirmParams: {
                        return_url: window.location.origin + confirmationUrl,
                    },
                });

                if (error) {
                    this.errorMessage = error.message;
                    this.isProcessing = false;
                } else {
                    // Payment will redirect automatically
                }
            } catch (error) {
                console.error('Order submission failed:', error);
                this.errorMessage = error.response?.data?.message || 'Order submission failed.';
                this.isProcessing = false;
            }
        },

        formatMoney(amount) {
            // Use existing money formatting logic or create simple formatter
            if (typeof money === 'function') {
                return money(amount);
            }
            return '$' + (amount).toFixed(2);
        },

        setupEventListeners() {
            // Listen for cart updates from other components
            Bus.$on('cart:updated', this.handleCartUpdate);

            // Listen for window events from Livewire/Alpine components
            this._cartRefreshHandler = () => this.loadCart();
            window.addEventListener('cart:refresh', this._cartRefreshHandler);
        },

        cleanupEventListeners() {
            Bus.$off('cart:updated', this.handleCartUpdate);
            if (this._cartRefreshHandler) {
                window.removeEventListener('cart:refresh', this._cartRefreshHandler);
            }
        },

        handleCartUpdate(cartData) {
            this.cart = cartData;
            // Re-initialize Stripe if total changed
            if (this.clientSecret) {
                this.initializeStripe();
            }
        },
    },
};
</script>

<style scoped>
.card-gg {
    /* Use existing card styles */
}

.card-summary {
    /* Use existing summary card styles */
}

.team-price {
    text-align: right;
}

.team-count {
    font-size: 14px;
    color: #666;
    margin-top: 10px;
}

.total-cost {
    font-weight: bold;
    font-size: 18px;
    margin: 15px 0;
}

.money {
    color: #28a745;
}
</style>
